import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { X, Clock, MapPin, Users, CheckCircle, XCircle, Plus, Trash2, RefreshCw, AlertTriangle, Ticket, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../i18n';
import { api } from '../api';
import { Avatar } from '../pages/Dashboard';
import { cn } from '../lib/utils';
import { TimePicker24h } from './TimePicker24h';

const DURATIONS = [
  { mins: 60, label: '1h' },
  { mins: 90, label: '1h30' },
  { mins: 120, label: '2h' },
];

const fmtDur = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h${m}`;
};

const buildPrivateTitle = (student) => {
  if (!student) return '';
  const initial = student.last_name?.[0]?.toUpperCase() || '';
  return initial ? `${student.first_name} ${initial}` : student.first_name;
};

export default function ClassModal({ classData, defaultDatetime, prefilledData, locations, allStudents, groupCourses, onClose, onSaved }) {
  const { t, lang } = useLang();
  const locale = lang === 'fr' ? fr : undefined;
  const navigate = useNavigate();
  const isEdit = !!classData?.id;

  const [form, setForm] = useState({
    type: classData?.type || prefilledData?.type || 'private',
    title: classData?.title || prefilledData?.title || '',
    datetime_start: classData?.datetime_start
      ? classData.datetime_start.substring(0, 16)
      : (prefilledData?.datetime_start || defaultDatetime || format(new Date(), "yyyy-MM-dd'T'18:00")),
    duration_minutes: classData?.duration_minutes || prefilledData?.duration_minutes || 90,
    location_id: classData?.location_id || prefilledData?.location_id || (locations[0]?.id || ''),
    notes: classData?.notes || '',
    group_course_id: classData?.group_course_id || prefilledData?.group_course_id || '',
    content: classData?.content || '',
  });

  const [attendances, setAttendances] = useState(
    (classData?.attendances || []).reduce((acc, a) => ({ ...acc, [a.student_id]: a.present }), {})
  );
  const [enrolledIds, setEnrolledIds] = useState(
    (classData?.attendances || []).map(a => a.student_id)
  );
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [addStudentSearch, setAddStudentSearch] = useState('');
  const [pendingStudentIds, setPendingStudentIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  // Private class extra state
  const [localStudents, setLocalStudents] = useState(allStudents);
  const [selectedPrivateStudentId, setSelectedPrivateStudentId] = useState(
    isEdit && classData?.attendances?.length === 1 ? classData.attendances[0].student_id : ''
  );
  const [titleAutoMode, setTitleAutoMode] = useState(!isEdit);
  const [studentSearch, setStudentSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(''); // '' | 'F' | 'L'
  const [showNotes, setShowNotes] = useState(!!(classData?.notes || classData?.content));
  const [showNewStudentForm, setShowNewStudentForm] = useState(false);
  const [newStudentForm, setNewStudentForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [newStudentSaving, setNewStudentSaving] = useState(false);
  const [privatePackages, setPrivatePackages] = useState({});
  const [teacherPricing, setTeacherPricing] = useState({ private_1p_1h: 110, group_1h30: 25 });
  const [sessionPrice, setSessionPrice] = useState(
    classData?.session_price != null ? String(classData.session_price) : ''
  );
  // v2.26 : prix horaire du privé selon le profil tarifaire de l'élève choisi (sinon tarif de base)
  const [rate, setRate] = useState(null);
  const [persons, setPersons] = useState(classData?.persons || 1);  // v2.30 : privé à deux
  useEffect(() => {
    if (!selectedPrivateStudentId) { setRate(null); return; }
    let alive = true;
    api.get(`/students/${selectedPrivateStudentId}/private-rate?persons=${persons}`).then(r => alive && setRate(r)).catch(() => alive && setRate(null));
    return () => { alive = false; };
  }, [selectedPrivateStudentId, persons]);

  useEffect(() => {
    api.get('/teacher').then(t => { if (t?.pricing) setTeacherPricing(t.pricing); }).catch(() => {});
    api.get('/packages').then(pkgs => {
      const map = {};
      pkgs.filter(p => p.type === 'private' && p.is_active).forEach(p => {
        if (!map[p.student_id]) map[p.student_id] = [];
        map[p.student_id].push(p);
      });
      setPrivatePackages(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (locations.length > 0 && !form.location_id) {
      setForm(f => ({ ...f, location_id: locations[0].id }));
    }
  }, [locations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Private student picker helpers — avec filtre F/L
  const filteredStudents = localStudents.filter(s => {
    const t = studentSearch.toLowerCase().trim();
    const phoneDigits = (s.phone || '').replace(/\D/g, '');
    const searchDigits = t.replace(/\D/g, '');
    const matchSearch = !t ||
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(t) ||
      (s.instagram || '').toLowerCase().includes(t) ||
      (searchDigits.length >= 4 && phoneDigits.includes(searchDigits));
    const matchRole = !roleFilter
      || (roleFilter === 'F' && (s.role === 'follower' || s.role === 'F'))
      || (roleFilter === 'L' && (s.role === 'leader' || s.role === 'L'));
    return matchSearch && matchRole;
  });
  const sortedStudents = [
    ...filteredStudents.filter(s => privatePackages[s.id]),
    ...filteredStudents.filter(s => !privatePackages[s.id]),
  ];

  const handleSelectPrivateStudent = (studentId) => {
    setSelectedPrivateStudentId(studentId);
    setEnrolledIds([studentId]);
    setAttendances({ [studentId]: true });
    if (titleAutoMode) {
      const student = localStudents.find(s => s.id === studentId);
      setForm(f => ({ ...f, title: buildPrivateTitle(student) }));
    }
  };

  const handleDurationChange = (mins) => {
    setForm(f => ({ ...f, duration_minutes: mins }));
  };

  const handleTypeChange = (type) => {
    setForm(f => ({ ...f, type }));
    if (type === 'private') { setTitleAutoMode(true); }
    else { setTitleAutoMode(false); }
  };

  const handleCreateStudent = async () => {
    if (!newStudentForm.first_name || !newStudentForm.last_name) {
      toast.error('Prénom et nom requis');
      return;
    }
    setNewStudentSaving(true);
    try {
      const created = await api.post('/students', newStudentForm);
      setLocalStudents(prev => [...prev, created]);
      // Use created object directly (avoid React async state batching issue)
      setSelectedPrivateStudentId(created.id);
      setEnrolledIds([created.id]);
      setAttendances({ [created.id]: true });
      if (titleAutoMode) {
        setForm(f => ({ ...f, title: buildPrivateTitle(created) }));
      }
      setShowNewStudentForm(false);
      setNewStudentForm({ first_name: '', last_name: '', email: '', phone: '' });
      toast.success('Élève créé');
    } catch {
      toast.error('Erreur lors de la création');
    } finally { setNewStudentSaving(false); }
  };

  const hourly = rate?.hourly ?? (persons >= 2 ? (teacherPricing.private_2p_1h || 120) : (teacherPricing.private_1p_1h || 110));
  const expectedPrice = Math.round(form.duration_minutes / 60 * hourly * 100) / 100;
  const durationChanged = isEdit && classData?.duration_minutes != null && Number(form.duration_minutes) !== Number(classData.duration_minutes);
  const isManualPrice = sessionPrice !== '' && Number(sessionPrice) !== expectedPrice;

  const handleSave = async (forceCreate = false) => {
    if (!form.location_id) { toast.error('Sélectionnez un lieu'); return; }
    if (form.type === 'private' && !isEdit && !selectedPrivateStudentId) {
      toast.error('Sélectionnez un élève'); return;
    }
    setSaving(true);
    try {
      let saved;
      const payload = {
        type: form.type,
        title: form.title,
        datetime_start: form.datetime_start,
        duration_minutes: parseInt(form.duration_minutes),
        location_id: form.location_id,
        notes: form.notes,
        content: form.content,
        group_course_id: form.group_course_id || null,
        ...(form.type === 'private' ? { session_price: sessionPrice !== '' ? parseFloat(sessionPrice) : expectedPrice, persons } : {}),
        force: forceCreate,
      };

      if (isEdit) {
        saved = await api.put(`/classes/${classData.id}`, payload);
        const attItems = enrolledIds.map(sid => ({
          student_id: sid,
          present: attendances[sid] !== undefined ? attendances[sid] : true,
          is_trial: false,
        }));
        if (attItems.length > 0) {
          await api.put(`/classes/${classData.id}/attendance`, attItems);
        }
        toast.success('Cours mis à jour');
      } else {
        saved = await api.post('/classes', payload);
        // For private: add primary student
        if (form.type === 'private' && selectedPrivateStudentId) {
          await api.post(`/classes/${saved.id}/students/${selectedPrivateStudentId}`);
        }
        toast.success('Cours créé');
      }
      onSaved(saved);
    } catch (e) {
      if (e.status === 409 && e.conflicts) {
        setConflictData(e.conflicts);
      } else {
        toast.error(e.message || 'Erreur');
      }
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce cours ?')) return;
    setDeleting(true);
    try {
      await api.delete(`/classes/${classData.id}`);
      toast.success('Cours supprimé');
      onSaved();
    } finally { setDeleting(false); }
  };

  const toggleAttendance = (sid) => setAttendances(a => ({ ...a, [sid]: !a[sid] }));

  const addStudentToClass = async (sid) => {
    if (enrolledIds.includes(sid)) return;
    if (isEdit) await api.post(`/classes/${classData.id}/students/${sid}`);
    setEnrolledIds(ids => [...ids, sid]);
    setAttendances(a => ({ ...a, [sid]: true }));
    setShowAddStudent(false);
  };

  const addStudentsToClass = async () => {
    if (pendingStudentIds.size === 0) return;
    for (const sid of pendingStudentIds) {
      if (enrolledIds.includes(sid)) continue;
      if (isEdit) await api.post(`/classes/${classData.id}/students/${sid}`);
      setEnrolledIds(ids => [...ids, sid]);
      setAttendances(a => ({ ...a, [sid]: true }));
    }
    setPendingStudentIds(new Set());
    setAddStudentSearch('');
    setShowAddStudent(false);
  };

  const removeStudent = async (sid) => {
    if (isEdit) await api.delete(`/classes/${classData.id}/students/${sid}`);
    setEnrolledIds(ids => ids.filter(id => id !== sid));
    setAttendances(a => { const n = { ...a }; delete n[sid]; return n; });
  };

  const enrolledStudents = localStudents.filter(s => enrolledIds.includes(s.id));
  const unenrolledStudents = localStudents.filter(s => !enrolledIds.includes(s.id));

  const fmtDate = (iso) => {
    try { return format(parseISO(iso), 'EEEE d MMMM · HH:mm', { locale }); } catch { return iso; }
  };

  const isPrivateCreate = !isEdit && form.type === 'private';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="class-modal">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2">
            <span className={cn('text-xs font-bold px-2.5 py-1 rounded-full',
              form.type === 'group' ? 'bg-[#0288D1]/10 text-[#0288D1]' : 'bg-[#26A69A]/10 text-[#26A69A]')}>
              {form.type === 'group' ? t('cls.group') : t('cls.private')}
            </span>
            {isEdit && <span className="text-sm font-semibold text-slate-600 capitalize">{fmtDate(form.datetime_start)}</span>}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="modal-close">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">

          {/* Type toggle — hidden when type is prefilled from parent */}
          {!isEdit && !prefilledData?.type && (
            <Field label={t('cls.type')}>
              <div className="flex gap-2">
                {[['group', t('cls.group')], ['private', t('cls.private')]].map(([val, label]) => (
                  <button key={val} onClick={() => handleTypeChange(val)}
                    className={cn('flex-1 h-10 rounded-xl text-sm font-bold transition-colors',
                      form.type === val ? 'bg-[#0288D1] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                    data-testid={`type-${val}`}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
          )}

          {/* ── Private: Student Picker (create mode) ── */}
          {isPrivateCreate && (
            <Field label="Élève">
              <div>
                {/* Filtre F/L + recherche sur la même ligne */}
                <div className="flex gap-2 mb-2">
                  <div className="flex gap-1 shrink-0">
                    {[['', 'Tous'], ['F', 'F'], ['L', 'L']].map(([val, label]) => (
                      <button key={val} onClick={() => setRoleFilter(val)}
                        className={cn('px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors border',
                          roleFilter === val
                            ? val === 'F' ? 'bg-pink-500 text-white border-pink-500'
                              : val === 'L' ? 'bg-[#0288D1] text-white border-[#0288D1]'
                              : 'bg-slate-700 text-white border-slate-700'
                            : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300')}
                        data-testid={`role-filter-${val || 'all'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <input
                    value={studentSearch}
                    onChange={e => setStudentSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="input-base flex-1"
                    data-testid="student-search-input"
                  />
                </div>
                <div className="border border-slate-200 rounded-xl max-h-44 overflow-y-auto divide-y divide-slate-100">
                  {sortedStudents.length === 0 && (
                    <p className="text-center py-5 text-slate-400 text-sm">Aucun élève trouvé</p>
                  )}
                  {sortedStudents.map(s => {
                    const hasPkg = privatePackages[s.id];
                    const isSelected = selectedPrivateStudentId === s.id;
                    const role = s.role === 'follower' || s.role === 'F' ? 'F' : s.role === 'leader' || s.role === 'L' ? 'L' : null;
                    return (
                      <button key={s.id} onClick={() => handleSelectPrivateStudent(s.id)}
                        className={cn('w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left', isSelected && 'bg-[#26A69A]/5')}
                        data-testid={`select-student-${s.id}`}>
                        <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800">{s.first_name} {s.last_name}
                            {role && <span className={cn('ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full', role === 'F' ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-[#0288D1]')}>{role}</span>}
                          </div>
                          {hasPkg && <div className="text-xs text-[#26A69A] font-semibold">Forfait privé actif</div>}
                        </div>
                        {isSelected && <CheckCircle size={15} className="text-[#26A69A] shrink-0" />}
                      </button>
                    );
                  })}
                </div>

                {/* Create new student toggle */}
                <button onClick={() => setShowNewStudentForm(v => !v)}
                  className="flex items-center gap-1.5 text-xs text-[#0288D1] font-semibold mt-2 px-1 hover:underline">
                  <Plus size={12} /> {showNewStudentForm ? 'Annuler' : 'Créer un nouvel élève'}
                </button>

                {showNewStudentForm && (
                  <div className="mt-2 p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2" data-testid="new-student-form">
                    <div className="grid grid-cols-2 gap-2">
                      {[['first_name','Prénom *'],['last_name','Nom *'],['email','Email'],['phone','Tél.']].map(([key, label]) => (
                        <div key={key}>
                          <label className="block text-xs text-slate-500 mb-0.5">{label}</label>
                          <input value={newStudentForm[key]}
                            onChange={e => setNewStudentForm(f => ({ ...f, [key]: e.target.value }))}
                            className="w-full h-9 px-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20"
                            data-testid={`new-student-${key}`} />
                        </div>
                      ))}
                    </div>
                    <button onClick={handleCreateStudent} disabled={newStudentSaving}
                      className="w-full h-9 bg-[#0288D1] text-white text-sm font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="create-new-student-btn">
                      {newStudentSaving ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
                      {newStudentSaving ? 'Création...' : "Créer l'élève"}
                    </button>
                  </div>
                )}
              </div>
            </Field>
          )}

          {/* Title — masqué pour les cours privés (auto-généré) */}
          {form.type !== 'private' && (
            <Field label={t('cls.title')}>
              <div className="relative">
                <input
                  value={form.title}
                  onChange={e => {
                    setTitleAutoMode(false);
                    setForm(f => ({ ...f, title: e.target.value }));
                  }}
                  placeholder="ex: Bachata débutants"
                  className="input-base pr-10"
                  data-testid="class-title-input"
                />
              </div>
            </Field>
          )}

          {/* Date + Heure — 2 colonnes égales */}
          <Field label={t('planning.dateTime')}>
            <div className="grid grid-cols-2 gap-2">
              <input type="date"
                value={form.datetime_start ? form.datetime_start.slice(0, 10) : ''}
                onChange={e => setForm(f => ({ ...f, datetime_start: e.target.value + 'T' + (f.datetime_start?.slice(11, 16) || '18:00') }))}
                className="input-base w-full" data-testid="class-date-input" />
              <TimePicker24h
                value={form.datetime_start?.slice(11, 16) || '18:00'}
                onChange={t => setForm(f => ({ ...f, datetime_start: (f.datetime_start?.slice(0, 10) || format(new Date(), 'yyyy-MM-dd')) + 'T' + t }))}
                data-testid="class-time-input" />
            </div>
          </Field>

          {/* Durée + Tarif sur la même ligne (pour privé) */}
          <Field label={form.type === 'private' ? `${t('cls.duration')} · Tarif` : t('cls.duration')}>
            <div className="flex gap-2 flex-wrap items-center">
              {DURATIONS.map(d => (
                <button key={d.mins} onClick={() => handleDurationChange(d.mins)}
                  className={cn('px-4 h-10 rounded-xl text-sm font-bold transition-colors',
                    form.duration_minutes === d.mins ? 'bg-[#0288D1] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                  data-testid={`duration-${d.mins}`}>
                  {d.label}
                </button>
              ))}
              <div className="flex items-center gap-1">
                <input type="number" value={form.duration_minutes}
                  onChange={e => handleDurationChange(parseInt(e.target.value) || 60)}
                  className="w-20 h-10 px-3 bg-slate-100 rounded-xl text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20"
                  min={30} step={15} data-testid="duration-custom" />
                <span className="text-sm text-slate-500">min</span>
              </div>
              {/* Tarif séance — sur la même ligne, pour privé */}
              {form.type === 'private' && (
                <div className="flex items-center gap-1.5 border-l border-slate-200 pl-2 ml-auto">
                  <input
                    type="number"
                    value={sessionPrice !== '' ? sessionPrice : expectedPrice}
                    onChange={e => setSessionPrice(e.target.value)}
                    className="w-20 h-10 px-2 text-right text-sm font-extrabold text-[#26A69A] bg-white border border-[#26A69A]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#26A69A]/20"
                    min={0} step="any"
                    data-testid="session-price-input"
                  />
                  <span className="text-sm font-bold text-[#26A69A]">CHF</span>
                </div>
              )}
            </div>
            {form.type === 'private' && (<>
              <div className="flex gap-1.5 mt-2" data-testid="private-persons">
                {[1, 2].map(n => (
                  <button key={n} type="button" onClick={() => { setPersons(n); if (!isEdit) setSessionPrice(''); }}
                    className={cn('h-8 px-3 rounded-lg text-xs font-bold border', persons === n ? 'bg-[#26A69A] text-white border-[#26A69A]' : 'bg-white text-slate-500 border-slate-200')}
                    data-testid={`persons-${n}`}>
                    {n === 1 ? '1 personne' : '2 personnes'}
                  </button>
                ))}
              </div>
              <div className="text-xs text-slate-400 mt-1" data-testid="private-price-calc">
                {fmtDur(form.duration_minutes)} × {hourly} CHF/h{persons >= 2 ? ' à deux' : ''}
                {rate?.profile && <span> ({rate.profile}{rate.source === 'base' ? ' → tarif de base' : ''})</span>}
                {isManualPrice && !durationChanged && (
                  <span className="ml-1 text-amber-600 font-semibold">· ✎ prix manuel{' '}
                    <button type="button" className="underline" onClick={() => setSessionPrice(isEdit ? String(expectedPrice) : '')}>recalculer</button>
                  </span>
                )}
                {durationChanged && isManualPrice && (
                  <div className="mt-1 text-amber-700 font-semibold" data-testid="price-proposal">
                    Nouvelle durée → {expectedPrice} CHF proposé{' '}
                    <button type="button" className="underline" onClick={() => setSessionPrice(String(expectedPrice))}>utiliser</button>
                    {' '}· ou garde {sessionPrice} CHF
                  </div>
                )}
              </div>
            </>)}
          </Field>

          {/* Location */}
          <Field label={t('cls.location')}>
            <select value={form.location_id} onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))}
              className="input-base" data-testid="class-location-select">
              <option value="">-- Choisir un lieu --</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.venue_name} · {l.city}</option>)}
            </select>
          </Field>

          {/* Attendance (edit mode) */}
          {isEdit && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">{t('cls.attendance')}</label>
                <button onClick={() => setShowAddStudent(s => !s)}
                  className="flex items-center gap-1 text-xs text-[#0288D1] font-semibold hover:bg-[#0288D1]/5 px-2 py-1 rounded-lg transition-colors"
                  data-testid="add-student-to-class-btn">
                  <Plus size={12} /> {t('cls.addStudent')}
                </button>
              </div>

              {showAddStudent && unenrolledStudents.length > 0 && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 mb-3">
                  <div className="p-2 border-b border-slate-200">
                    <input
                      type="text" value={addStudentSearch}
                      onChange={e => setAddStudentSearch(e.target.value)}
                      placeholder="Rechercher..."
                      autoFocus
                      className="w-full h-8 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20"
                      data-testid="add-student-search-input"
                    />
                  </div>
                  <div className="max-h-36 overflow-y-auto divide-y divide-slate-100">
                    {unenrolledStudents
                      .filter(s => {
                        const t = addStudentSearch.toLowerCase().trim();
                        if (!t) return true;
                        const ph = (s.phone || '').replace(/\D/g, '');
                        const st = t.replace(/\D/g, '');
                        return `${s.first_name} ${s.last_name}`.toLowerCase().includes(t) ||
                          (s.instagram || '').toLowerCase().includes(t) ||
                          (st.length >= 4 && ph.includes(st));
                      })
                      .map(s => {
                        const isPending = pendingStudentIds.has(s.id);
                        return (
                          <button key={s.id}
                            onClick={() => setPendingStudentIds(ids => { const n = new Set(ids); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; })}
                            className={cn('w-full flex items-center gap-3 px-3 py-2 hover:bg-white transition-colors text-left', isPending && 'bg-[#0288D1]/5')}
                            data-testid={`add-student-${s.id}`}>
                            <div className={cn('w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors', isPending ? 'bg-[#0288D1] border-[#0288D1]' : 'border-slate-300')}>
                              {isPending && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>}
                            </div>
                            <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" />
                            <span className="text-sm font-medium text-slate-800">{s.first_name} {s.last_name}</span>
                          </button>
                        );
                      })}
                  </div>
                  {pendingStudentIds.size > 0 && (
                    <div className="p-2 border-t border-slate-200">
                      <button onClick={addStudentsToClass}
                        className="w-full h-8 rounded-lg bg-[#0288D1] text-white text-sm font-bold"
                        data-testid="confirm-add-students-btn">
                        Ajouter {pendingStudentIds.size} élève{pendingStudentIds.size > 1 ? 's' : ''}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {enrolledStudents.length === 0 ? (
                <div className="text-center py-4 text-slate-400 text-sm">{t('cls.noStudents')}</div>
              ) : (
                <div className="space-y-2">
                  {enrolledStudents.map(s => {
                    const present = attendances[s.id] !== undefined ? attendances[s.id] : true;
                    return (
                      <div key={s.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2.5" data-testid={`attendance-student-${s.id}`}>
                        <div className="flex items-center gap-2">
                          <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" />
                          {isEdit && classData?.type === 'private' ? (
                            <button
                              onClick={() => { onClose(); navigate(`/students/${s.id}`); }}
                              className="text-sm font-medium text-[#0288D1] hover:underline underline-offset-2 transition-colors"
                              data-testid={`student-profile-link-${s.id}`}
                            >
                              {s.first_name} {s.last_name}
                            </button>
                          ) : (
                            <span className="text-sm font-medium text-slate-800">{s.first_name} {s.last_name}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => toggleAttendance(s.id)}
                            className={cn('flex items-center gap-1 px-3 h-8 rounded-lg text-xs font-bold transition-colors',
                              present ? 'bg-[#26A69A]/10 text-[#26A69A]' : 'bg-red-50 text-red-400')}
                            data-testid={`toggle-presence-${s.id}`}>
                            {present ? <><CheckCircle size={14} /> {t('cls.present')}</> : <><XCircle size={14} /> {t('cls.absent')}</>}
                          </button>
                          <button onClick={() => removeStudent(s.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors">
                            <Trash2 size={12} className="text-red-400" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Tarif séance (edit mode) — masqué si privé car déjà dans le champ Durée */}
            </div>
          )}

          {/* Notes — champ unique fusionné (Content + Notes), masqué par défaut */}
          {form.type === 'private' ? (
            <div>
              {!showNotes ? (
                <button onClick={() => setShowNotes(true)}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#0288D1] font-semibold transition-colors"
                  data-testid="toggle-notes-btn">
                  <Plus size={12} /> Ajouter des notes
                </button>
              ) : (
                <Field label="Notes">
                  <div className="relative">
                    <textarea
                      value={form.notes || form.content}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value, content: e.target.value }))}
                      rows={3} placeholder="Observations, contenu du cours, remarques..."
                      autoFocus
                      className="input-base resize-none pr-8" data-testid="class-notes-input" />
                    <button onClick={() => { setShowNotes(false); setForm(f => ({ ...f, notes: '', content: '' })); }}
                      className="absolute right-2 top-2 text-slate-300 hover:text-slate-500 transition-colors">
                      <X size={14} />
                    </button>
                  </div>
                </Field>
              )}
            </div>
          ) : (
            <>
              <Field label={t('groupClasses.content')}>
                <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  rows={2} placeholder="ex: Travail sur les tours, musicality..."
                  className="input-base resize-none" data-testid="class-content-input" />
              </Field>
              <Field label={t('cls.notes')}>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2} placeholder="Observations, remarques..."
                  className="input-base resize-none" data-testid="class-notes-input" />
              </Field>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-3 border-t border-slate-100 shrink-0">
          <div className="flex gap-3">
            {isEdit && (
              <button onClick={handleDelete} disabled={deleting}
                className="h-12 px-4 rounded-xl bg-red-50 text-red-500 font-bold text-sm hover:bg-red-100 transition-colors"
                data-testid="delete-class-btn">
                {deleting ? '...' : <Trash2 size={18} />}
              </button>
            )}
            <button onClick={onClose} className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm hover:bg-slate-200 transition-colors">
              {t('common.cancel')}
            </button>
            <button onClick={() => handleSave(false)} disabled={saving}
              className="flex-1 h-12 rounded-xl bg-[#0288D1] text-white font-bold text-sm hover:bg-[#0277BE] active:scale-95 transition-all shadow-sm"
              data-testid="save-class-btn">
              {saving ? '...' : t('common.save')}
            </button>
          </div>
        </div>
      </div>

      {/* Conflict warning modal */}
      {conflictData && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <div>
                <div className="font-bold text-slate-900">Conflit détecté</div>
                <div className="text-xs text-slate-500">Ce créneau chevauche avec :</div>
              </div>
            </div>
            <div className="space-y-1.5 mb-5">
              {conflictData.map((c, i) => (
                <div key={c.id || c.title || i} className="flex items-center gap-2 bg-amber-50 rounded-xl px-3 py-2 text-xs">
                  {c.type === 'festival'
                    ? <><Ticket size={13} className="text-amber-600 shrink-0" /><span className="font-semibold">{c.name}</span><span className="text-slate-400 ml-auto">{c.date_start} → {c.date_end}</span></>
                    : <><Calendar size={13} className="text-slate-500 shrink-0" /><span className="font-semibold">{c.title}</span><span className="text-slate-400 ml-auto">{(c.datetime_start || '').slice(11, 16)}</span></>
                  }
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConflictData(null)} className="flex-1 h-11 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm">Annuler</button>
              <button
                onClick={() => { setConflictData(null); handleSave(true); }}
                className="flex-1 h-11 rounded-xl bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm"
                data-testid="force-save-btn"
              >
                Forcer quand même
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input-base {
          width: 100%;
          min-height: 2.5rem;
          padding: 0.5rem 0.75rem;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          outline: none;
          transition: all 0.15s;
        }
        .input-base:focus {
          border-color: #0288D1;
          box-shadow: 0 0 0 3px rgba(2, 136, 209, 0.1);
          background: white;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">{label}</label>
      {children}
    </div>
  );
}
